import {
  defineQueries,
  type ExpressionFactory,
  type Query,
} from "@rocicorp/zero";

export const WORKFLOW_RUNS_LIMIT = 250;

import * as z from "zod";
import {
  FEDERATED_API_KEY_NAME_PREFIX,
  USER_API_KEY_NAME_PREFIX,
} from "./apiKeyPrefix";
import {
  principalMatch,
  canReadApiKey,
  canReadAuditLog,
  canReadAuditLogDestination,
  canReadBranch,
  canReadConnector,
  canReadDeletedWorkflow,
  canReviewGrantRequest,
  canReadDuplicateWorkflowPair,
  canReadDuplicateWorkflowReview,
  canReadEgressRule,
  canReadNetwork,
  canReadOAuthApp,
  canReadOidcProvider,
  canReadRun,
  canReadServiceAccount,
  canReadSkill,
  canReadSpace,
  canReadSpaceAsMemberOrDiscover,
  canReadSsoProvider,
  canReadWorkflow,
  canReadWorkflowFavorite,
  canReadWorkflowView,
  canReadPersonalChat,
  canReadPersonalChats,
  isHumanManagedServiceAccountKey,
  tenantGrantExists,
} from "./authorization";
import {
  type BranchContributor,
  type ChatEntry,
  type CommitRoute,
  type ConnectedAppUsage,
  type RoleGrant,
  type Schema,
  type Space,
  type Workflow,
  type WorkflowBranch,
  type WorkflowCommit,
  zql,
} from "./generatedDrizzleSchema";
import { escapeLikePattern } from "./likePattern";
import { ALLOW_ALL_ROLE_IDS } from "./rbac/roles";
import { defineTenantQuery } from "./defineTenantQuery";

// Shared relation builders for workflow data. The Query<Table, Schema, Row>
// types give us proper inference through each chain, so the extracted callbacks
// preserve full type safety at every call site.

const fullChatEntries = (e: Query<"chatEntries", Schema, ChatEntry>) =>
  e
    .orderBy("createdAt", "asc")
    .related("userMessage", (um) =>
      um.related("user").related("serviceAccount")
    )
    .related("assistantMessage")
    .related("toolCall")
    .related("assistantReasoning")
    .related("systemMessage")
    .related("userAction", (ua) => ua.related("user"))
    .related("compaction")
    .related("attachments");

const withPersonalSpaceOwner = (
  s: Query<"spaces", Schema, Space | undefined>
) => s.related("roleGrantsAsScope", (rg) => rg.related("user").limit(1));

const recentContributors =
  (since: number) =>
  (c: Query<"branchContributors", Schema, BranchContributor>) =>
    c
      .where("lastMessagedAt", ">=", since)
      .related("user")
      .orderBy("lastMessagedAt", "desc")
      .limit(25);

export function roundedHoursAgo(hours: number): number {
  const oneHour = 60 * 60 * 1000;
  return Math.floor((Date.now() - hours * oneHour) / oneHour) * oneHour;
}

// The connectors a commit's steps reference. commit_step usages supersede the
// legacy commit_connected_apps table, which no longer has a live write path.
const commitStepUsages = (
  u: Query<"connectedAppUsages", Schema, ConnectedAppUsage>
) => u.where("type", "commit_step");

// A duplicate-pair side with everything its review card renders: space badge,
// live-branch badge state, and connector avatars.
const monitorPairWorkflow = (
  w: Query<"workflows", Schema, Workflow | undefined>
) =>
  w.related("space", withPersonalSpaceOwner).related("branches", (b) =>
    b
      .where("name", "main")
      .where("headCommitId", "IS NOT", null)
      .related(
        "headCommit",
        (c: Query<"workflowCommits", Schema, WorkflowCommit | undefined>) =>
          c
            .related("appUsages", (u) =>
              commitStepUsages(u).related("connectedApp", (a) =>
                a.related("oauthConnection")
              )
            )
            .one()
      )
      .one()
  );

const listRowBranch =
  (activitySince: number) =>
  (b: Query<"workflowBranches", Schema, WorkflowBranch>) =>
    b
      .related("user")
      .related("contributors", recentContributors(activitySince))
      .related("headCommit", (c) =>
        c
          // TODO(expand/contract): remove once no cached client reads
          // `connectedApps`; superseded by `appUsages`.
          .related("connectedApps")
          .related("appUsages", commitStepUsages)
          .one()
      );

const featured = (r: Query<"commitRoutes", Schema, CommitRoute>) =>
  r.where("routeType", "IS NOT", null);

const workflowBranches =
  (userId: string) => (b: Query<"workflowBranches", Schema, WorkflowBranch>) =>
    b
      .where(({ or, cmp }) => or(cmp("name", "main"), cmp("userId", userId)))
      .related("user")
      .related("headCommit", (c) =>
        c
          // TODO(expand/contract): remove once no cached client reads
          // `connectedApps`; superseded by `appUsages`.
          .related("connectedApps")
          .related("appUsages", commitStepUsages)
          .related("routes", featured)
          .related("schedules")
          .related("links")
          .one()
      )
      .orderBy("createdAt", "asc");

const workflowBranchesForLinks =
  (userId: string, branchId?: string) =>
  (b: Query<"workflowBranches", Schema, WorkflowBranch>) => {
    const base = branchId ? b.where("id", branchId) : b;
    return base
      .where(({ or, cmp }) => or(cmp("name", "main"), cmp("userId", userId)))
      .related("user")
      .related("headCommit", (c) =>
        c
          // TODO(expand/contract): remove once no cached client reads
          // `connectedApps`; superseded by `appUsages`.
          .related("connectedApps")
          .related("appUsages", commitStepUsages)
          .related("routes", featured)
          .related("schedules")
          .related("links")
          .one()
      )
      .orderBy("createdAt", "asc");
  };

const workflowBranchSummaries = (
  b: Query<"workflowBranches", Schema, WorkflowBranch>
) =>
  b
    .related("user")
    .related("contributors", (c) => c.related("user"))
    .related("commits", (c) => c.where("message", "IS NOT", null))
    .related("spaceBranchMembers", (m) => m.where("deletedAt", "IS NOT", null))
    .orderBy("createdAt", "asc");

const roleGrantsWithMembers = (g: Query<"roleGrants", Schema, RoleGrant>) =>
  g
    .related("customRole", (cr) => cr.related("rolePermissions"))
    .related("userGroup", (ug) =>
      ug.related("roleGrantsAsScope", (mg) => mg.related("user")).one()
    )
    .related("user");

const roleGrantsForAccessCheck =
  (userId: string) => (g: Query<"roleGrants", Schema, RoleGrant>) =>
    g
      .related("customRole", (cr) => cr.related("rolePermissions"))
      .related("userGroup", (ug) =>
        ug
          .related("roleGrantsAsScope", (mg) => mg.where("userId", userId))
          .one()
      )
      .related("serviceAccount", (sa) => sa.one());

// Only the permission detail the access map's availability check reads, dropping
// the user/userGroup/serviceAccount relations the list-page grant builders carry.
const leanScopeGrants = (g: Query<"roleGrants", Schema, RoleGrant>) =>
  g.related("customRole", (cr) => cr.related("rolePermissions"));

export const queries = defineQueries({
  allUsers: defineTenantQuery(() => zql.user.orderBy("createdAt", "asc")),

  spaceMembers: defineTenantQuery(
    z.object({ spaceId: z.string() }),
    ({ args, ctx }) =>
      roleGrantsWithMembers(
        zql.roleGrants
          .where("scopeId", args.spaceId)
          .whereExists("scopeAsSpace", (s) => s.where(canReadSpace(ctx)))
      )
  ),

  userBranchPresence: defineTenantQuery(
    z.object({ since: z.number().optional() }).default({}),
    ({ args, ctx }) =>
      zql.userBranchPresence
        .where("lastSeenAt", ">", args.since ?? 0)
        .whereExists("branch", (b) => b.where(canReadBranch(ctx)), {
          scalar: true,
        })
        .related("user")
  ),

  currentUser: defineTenantQuery(({ ctx }) =>
    zql.user.where("id", ctx.userId).one()
  ),

  currentTenant: defineTenantQuery(() => zql.tenants.one()),

  personalSpace: defineTenantQuery(({ ctx }) =>
    zql.spaces
      .where("mode", "personal")
      .whereExists("roleGrantsAsScope", (rg) => rg.where("userId", ctx.userId))
      .one()
  ),

  personalSpaceOwners: defineTenantQuery(({ ctx }) =>
    zql.spaces
      .where("mode", "personal")
      .where(canReadSpace(ctx))
      .related("roleGrantsAsScope", (rg) => rg.related("user").limit(1))
  ),

  spaces: defineTenantQuery(
    z.object({
      settingsView: z.boolean(),
    }),
    ({ ctx, args: { settingsView } }) =>
      zql.spaces
        .where(
          settingsView ? canReadSpace(ctx) : canReadSpaceAsMemberOrDiscover(ctx)
        )
        .related("roleGrantsAsScope", leanScopeGrants)
        .orderBy("name", "asc")
  ),

  // Only relation is the current user's own grants on each space, so the UI
  // can tell member from discoverable-joiner without a second query.
  spacesLite: defineTenantQuery(
    z.object({ includeAdminSpaces: z.boolean().optional() }),
    ({ ctx, args: { includeAdminSpaces } }) =>
      zql.spaces
        .where(
          includeAdminSpaces
            ? canReadSpace(ctx)
            : canReadSpaceAsMemberOrDiscover(ctx)
        )
        .related("roleGrantsAsScope", (rg) =>
          rg.where(principalMatch(ctx.userId))
        )
        .orderBy("name", "asc")
  ),

  // Spaces with the grant detail needed to render the Settings access-map
  // visualization. Availability only — no user or other display relations. When
  // `spaceIds` is given the result is scoped to those spaces; otherwise it
  // covers every readable space. `canReadSpace` stays the authorization guard
  // regardless of the ids passed.
  spaceAvailabilityGrants: defineTenantQuery(
    z.object({ spaceIds: z.array(z.string()).optional() }).default({}),
    ({ ctx, args: { spaceIds } }) =>
      (spaceIds === undefined
        ? zql.spaces.where(canReadSpace(ctx))
        : zql.spaces.where((eb) =>
            eb.and(canReadSpace(ctx)(eb), eb.cmp("id", "IN", spaceIds))
          )
      ).related("roleGrantsAsScope", (rg) =>
        rg.related("customRole", (cr) => cr.related("rolePermissions"))
      )
  ),

  // Avoid relations to keep snappy
  skillsLite: defineTenantQuery(({ ctx }) =>
    zql.skills.where(canReadSkill(ctx)).orderBy("name", "asc")
  ),

  // Avoid most relations
  connectedAppsLite: defineTenantQuery(({ ctx }) =>
    zql.connectedApps
      .where(canReadConnector(ctx))
      .orderBy("name", "asc")
      .related("oauthConnection")
      .related("createdBy")
  ),

  userPreferences: defineTenantQuery(({ ctx }) =>
    zql.userPreferences.where("userId", ctx.userId).one()
  ),

  allStyles: defineTenantQuery(() => zql.designs.orderBy("createdAt", "asc")),

  tenantCustomDomains: defineTenantQuery(() =>
    zql.tenantCustomDomains.orderBy("createdAt", "asc")
  ),

  connectedAppsIndex: defineTenantQuery(({ ctx }) =>
    zql.connectedApps
      .where("fresh", false)
      .where(canReadConnector(ctx))
      .orderBy("name", "asc")
      .related("oauthConnection")
      .related("createdBy")
      .related("roleGrantsAsScope", roleGrantsForAccessCheck(ctx.userId))
      .related("usages", (u) => u.where("type", "!=", "commit_step"))
  ),

  // Pending share requests visible to the current user — their own, plus every
  // request they can review. Powers the pinned group on the connectors list.
  pendingGrantRequests: defineTenantQuery(({ ctx }) =>
    zql.grantRequests
      .where(canReviewGrantRequest(ctx))
      .orderBy("createdAt", "desc")
      .related("requestedByUser")
      .related("requestedGrants")
      .related("scopeAsConnector", (c) => c.related("oauthConnection"))
  ),

  // The pending request (if any) for one scope — the connector modal's banner
  // and the requester's pending state.
  grantRequestForScope: defineTenantQuery(
    z.object({ scopeId: z.string() }),
    ({ args, ctx }) =>
      zql.grantRequests
        .where("scopeId", args.scopeId)
        .where(canReviewGrantRequest(ctx))
        .orderBy("createdAt", "desc")
        .related("requestedByUser")
        .related("requestedGrants")
        .one()
  ),

  skillsIndex: defineTenantQuery(({ ctx }) =>
    zql.skills
      .where("fresh", false)
      .where(canReadSkill(ctx))
      .orderBy("name", "asc")
      .related("createdBy")
      .related("gitInstallation", (installation) =>
        installation.related("source")
      )
      .related("roleGrantsAsScope", roleGrantsForAccessCheck(ctx.userId))
  ),

  skillsManagementIndex: defineTenantQuery(({ ctx }) =>
    zql.skills
      .where(canReadSkill(ctx))
      .orderBy("name", "asc")
      .related("createdBy")
      .related("gitInstallation", (installation) =>
        installation.related("source")
      )
      .related("roleGrantsAsScope", roleGrantsForAccessCheck(ctx.userId))
  ),

  networksIndex: defineTenantQuery(({ ctx }) =>
    zql.networks
      .where("disabledAt", "IS", null)
      .where(canReadNetwork(ctx))
      .orderBy("name", "asc")
      .related("createdBy")
      .related("roleGrantsAsScope", roleGrantsForAccessCheck(ctx.userId))
  ),

  // Lean projections for the access map: the same rows as the *Index queries but
  // only the grant detail the graph reads, dropping the list-page relations
  // (usages, createdBy, principal grants) it never touches. oauthConnection
  // stays because ConnectorLogo's prop type requires it.
  accessGraphConnectedApps: defineTenantQuery(({ ctx }) =>
    zql.connectedApps
      .where("fresh", false)
      .where(canReadConnector(ctx))
      .orderBy("name", "asc")
      .related("oauthConnection")
      .related("roleGrantsAsScope", leanScopeGrants)
  ),

  accessGraphSkills: defineTenantQuery(({ ctx }) =>
    zql.skills
      .where("fresh", false)
      .where(canReadSkill(ctx))
      .orderBy("name", "asc")
      .related("roleGrantsAsScope", leanScopeGrants)
  ),

  accessGraphGroups: defineTenantQuery(() =>
    zql.userGroups
      // Member `user` is kept for the invite-member modal's face piles (reached
      // from the access map); only the unused principal grants are dropped.
      .related("roleGrantsAsScope", (mg) => mg.related("user"))
      .orderBy("name", "asc")
  ),

  loadSkill: defineTenantQuery(
    z.object({ skillId: z.string() }),
    ({ args, ctx }) =>
      zql.skills
        .where("id", args.skillId)
        .where(canReadSkill(ctx))
        .related("createdBy")
        .related("gitInstallation", (installation) =>
          installation.related("source", (source) =>
            source.related("connectedApp", (app) =>
              app.where(canReadConnector(ctx))
            )
          )
        )
        .related("roleGrantsAsScope", roleGrantsWithMembers)
        .one()
  ),

  loadSkillFiles: defineTenantQuery(
    z.object({ skillId: z.string() }),
    ({ args, ctx }) =>
      zql.skillFiles
        .where("skillId", args.skillId)
        .whereExists("skill", (skill) => skill.where(canReadSkill(ctx)))
        .orderBy("path", "asc")
        .related("blob")
  ),

  loadSkillFileIndex: defineTenantQuery(
    z.object({ skillId: z.string() }),
    ({ args, ctx }) =>
      zql.skillFiles
        .where("skillId", args.skillId)
        .whereExists("skill", (skill) => skill.where(canReadSkill(ctx)))
        .orderBy("path", "asc")
  ),

  loadSkillFile: defineTenantQuery(
    z.object({ skillId: z.string(), path: z.string() }),
    ({ args, ctx }) =>
      zql.skillFiles
        .where("skillId", args.skillId)
        .where("path", args.path)
        .whereExists("skill", (skill) => skill.where(canReadSkill(ctx)))
        .related("blob")
        .one()
  ),

  loadSkillGitSource: defineTenantQuery(
    z.object({ sourceId: z.string() }),
    ({ args, ctx }) =>
      zql.skillGitSources
        .where("id", args.sourceId)
        .where("createdByUserId", ctx.userId)
        .related("connectedApp", (app) => app.where(canReadConnector(ctx)))
        .related("installations", (installation) =>
          installation.related("skill")
        )
        .one()
  ),

  skillGroupAccess: defineTenantQuery(
    z.object({ skillId: z.string() }),
    ({ args, ctx }) =>
      zql.skills
        .where("id", args.skillId)
        .where(canReadSkill(ctx))
        .related("roleGrantsAsScope", (g) =>
          g
            .related("userGroup", (ug) => ug.related("roleGrantsAsPrincipal"))
            .related("user", (u) => u.related("roleGrantsAsPrincipal"))
        )
        .one()
  ),

  workflowAppSlugs: defineTenantQuery(
    z.object({ workflowId: z.string() }),
    ({ args, ctx }) =>
      zql.connectedAppUsages
        .where("type", "workflow_slug")
        .where("refId", args.workflowId)
        .whereExists("workflow", (w) => w.where(canReadWorkflow(ctx)))
  ),

  // Maps workflow ids to their space, for the client-side "connectors used by a
  // space" preflight (parity with spaceAppEdgesZql). Gated on canReadSpace and
  // unfiltered by deletedAt to match the server's "every workflow in the space".
  workflowSpaceMap: defineTenantQuery(
    z.object({ workflowIds: z.array(z.string()) }),
    ({ args, ctx }) =>
      zql.workflows
        .where("id", "IN", args.workflowIds)
        .whereExists("space", (s) => s.where(canReadSpace(ctx)))
  ),

  allWorkflows: defineTenantQuery(({ ctx }) =>
    zql.workflows.where(canReadWorkflow(ctx))
  ),

  workflowsByCodeSearch: defineTenantQuery(
    z.object({ searchText: z.string() }),
    ({ args, ctx }) => {
      const searchText = args.searchText.trim();
      if (!searchText) return zql.workflows.where("id", "");

      const pattern = `%${escapeLikePattern(searchText)}%`;
      return zql.workflows
        .where(canReadWorkflow(ctx))
        .whereExists("branches", (b) =>
          b
            .where(({ or, cmp }) =>
              or(cmp("name", "main"), cmp("userId", ctx.userId))
            )
            .whereExists("headCommit", (c) =>
              c.whereExists("files", (f) =>
                f.whereExists("blob", (blob) =>
                  blob.where("content", "ILIKE", pattern)
                )
              )
            )
        );
    }
  ),

  spaceDeletionPreviewWorkflows: defineTenantQuery(
    z.object({ spaceId: z.string() }),
    ({ args, ctx }) =>
      zql.workflows
        .where("spaceId", args.spaceId)
        .where(canReadWorkflow(ctx))
        .orderBy("createdAt", "desc")
  ),

  autofixWorkflows: defineTenantQuery(({ ctx }) =>
    zql.workflows
      .where(canReadWorkflow(ctx))
      .where("autofixBranchId", "IS NOT", null)
  ),

  // Workflows with an open Autofix/Autotune branch awaiting review, behind the
  // monitoring Autofix and Autotune card. The AI monitors rename the branch
  // shortly after creating it, so we can't filter branches by name; the card
  // matches each workflow's autofixBranchId/autoImproveBranchId pointer to its
  // branch. A push or reject clears the pointer, which drops the row.
  monitorAutofixAutotune: defineTenantQuery(({ ctx }) =>
    zql.workflows
      .where(canReadWorkflow(ctx))
      .where("deletedAt", "IS", null)
      .where(({ or, cmp }) =>
        or(
          cmp("autofixBranchId", "IS NOT", null),
          cmp("autoImproveBranchId", "IS NOT", null)
        )
      )
      .related("space", withPersonalSpaceOwner)
      .related("branches")
  ),

  // Every workflows-monitoring panel rides this one query, and rooting it at
  // spaces runs the read-permission probe once per space instead of once per
  // workflow.
  monitorSpaceWorkflowRuns: defineTenantQuery(
    z.object({ since: z.number() }),
    ({ args, ctx }) =>
      zql.spaces
        .where(canReadSpace(ctx))
        .related("roleGrantsAsScope", (rg) => rg.related("user").limit(1))
        .related("workflows", (w) =>
          w
            .where("deletedAt", "IS", null)
            .related("runDailyStats", (s) => s.where("day", ">=", args.since))
            .related("branches", (b) =>
              b
                .where("name", "main")
                .related("workflowRuns", (r) =>
                  r
                    .where("ephemeral", false)
                    .orderBy("createdAt", "desc")
                    .limit(14)
                )
                .related(
                  "headCommit",
                  (
                    c: Query<
                      "workflowCommits",
                      Schema,
                      WorkflowCommit | undefined
                    >
                  ) => c.related("routes").related("dependencies").one()
                )
                .one()
            )
        )
  ),

  // Superseded by monitorSpaceWorkflowRuns (which drops the stepActivity
  // relation); kept for cached clients until the contract phase.
  monitorSpaceWorkflows: defineTenantQuery(
    z.object({ since: z.number() }),
    ({ args, ctx }) =>
      zql.spaces
        .where(canReadSpace(ctx))
        .related("roleGrantsAsScope", (rg) => rg.related("user").limit(1))
        .related("workflows", (w) =>
          w
            .where("deletedAt", "IS", null)
            .related("runDailyStats", (s) => s.where("day", ">=", args.since))
            .related("stepActivity")
            .related("branches", (b) =>
              b
                .where("name", "main")
                .related("workflowRuns", (r) =>
                  r
                    .where("ephemeral", false)
                    .orderBy("createdAt", "desc")
                    .limit(14)
                )
                .related(
                  "headCommit",
                  (
                    c: Query<
                      "workflowCommits",
                      Schema,
                      WorkflowCommit | undefined
                    >
                  ) => c.related("routes").related("dependencies").one()
                )
                .one()
            )
        )
  ),

  // Superseded by monitorSpaceWorkflows; kept for cached clients until the
  // contract phase.
  monitorWorkflowRunStatuses: defineTenantQuery(({ ctx }) =>
    zql.workflows
      .where(canReadWorkflow(ctx))
      .related("space", withPersonalSpaceOwner)
      .related("branches", (b) =>
        b
          .where("name", "main")
          .related("workflowRuns", (r) =>
            r.where("ephemeral", false).orderBy("createdAt", "desc").limit(14)
          )
          .one()
      )
  ),

  monitorWorkflowMainRuns: defineTenantQuery(({ ctx }) =>
    zql.workflows
      .where(canReadWorkflow(ctx))
      .related("space", withPersonalSpaceOwner)
      .related("branches", (b) =>
        b
          .where("name", "main")
          .related("workflowRuns", (r) =>
            r.where("ephemeral", false).orderBy("createdAt", "desc").limit(14)
          )
          .one()
      )
  ),

  monitorRoutes: defineTenantQuery(({ ctx }) =>
    zql.workflows
      .where(canReadWorkflow(ctx))
      .where("deletedAt", "IS", null)
      .related("space", withPersonalSpaceOwner)
      .related("branches", (b) =>
        b
          .where("name", "main")
          .where("headCommitId", "IS NOT", null)
          .related(
            "headCommit",
            (c: Query<"workflowCommits", Schema, WorkflowCommit | undefined>) =>
              c.related("routes").one()
          )
          .one()
      )
  ),

  // Superseded by monitorSpaceWorkflows; kept for cached clients until the
  // contract phase.
  monitorHeadCommits: defineTenantQuery(({ ctx }) =>
    zql.workflows
      .where(canReadWorkflow(ctx))
      .related("space", withPersonalSpaceOwner)
      .related("branches", (b) =>
        b
          .where("name", "main")
          .where("headCommitId", "IS NOT", null)
          .related(
            "headCommit",
            (c: Query<"workflowCommits", Schema, WorkflowCommit | undefined>) =>
              c.related("routes").related("dependencies").one()
          )
          .one()
      )
  ),

  monitorDuplicatePairs: defineTenantQuery(({ ctx }) =>
    zql.duplicateWorkflowPairs
      .where("verdict", "duplicate")
      .where(canReadDuplicateWorkflowPair(ctx))
      .orderBy("judgedAt", "desc")
      .related("workflowA", monitorPairWorkflow)
      .related("workflowB", monitorPairWorkflow)
  ),

  monitorDuplicateReviews: defineTenantQuery(({ ctx }) =>
    zql.duplicateWorkflowReviews.where(canReadDuplicateWorkflowReview(ctx))
  ),

  monitorDuplicateDetectionRun: defineTenantQuery(() =>
    zql.duplicateDetectionRuns.one()
  ),

  // Nesting stats under workflows runs the read-permission probe once per
  // workflow instead of once per (workflow, day) row.
  monitorWorkflowDailyStats: defineTenantQuery(
    z.object({ since: z.number() }),
    ({ args, ctx }) =>
      zql.workflows
        .where(canReadWorkflow(ctx))
        .related("runDailyStats", (s) => s.where("day", ">=", args.since))
  ),

  monitoringStorageBySpace: defineTenantQuery(({ ctx }) =>
    zql.spaceStorageStats.whereExists("space", (s) =>
      s.where(canReadSpace(ctx))
    )
  ),

  monitoringExecutionStatsBySpace: defineTenantQuery(
    z.object({ since: z.number() }),
    ({ args, ctx }) =>
      zql.spaces.where(canReadSpace(ctx)).related("executionStats", (stats) =>
        stats
          .where("bucketAt", ">=", args.since)
          .where("source", "IS NOT", null)
          .where(({ or, cmp }) =>
            or(
              cmp("source", "!=", "workflow"),
              cmp("workflowId", "IS NOT", null)
            )
          )
          .orderBy("bucketAt", "asc")
      )
  ),

  monitoringNetworkUsageBySpace: defineTenantQuery(
    z.object({ since: z.number() }),
    ({ args, ctx }) =>
      zql.spaces
        .where(canReadSpace(ctx))
        .related("networkUsageBuckets", (usage) =>
          usage.where("bucketAt", ">=", args.since).orderBy("bucketAt", "asc")
        )
  ),

  monitoringTenantNetworkUsage: defineTenantQuery(
    z.object({ since: z.number() }),
    ({ args, ctx }) =>
      zql.networkUsageBuckets
        .where("bucketAt", ">=", args.since)
        .where(
          tenantGrantExists(
            ctx,
            "tenant:spaces_read"
          ) as unknown as ExpressionFactory<"networkUsageBuckets", Schema>
        )
        .orderBy("bucketAt", "asc")
  ),

  monitoringTenantExecutionCapacity: defineTenantQuery(
    z.object({ since: z.number() }),
    ({ args, ctx }) =>
      zql.executionStats
        .where("bucketAt", ">=", args.since)
        .where("source", "IS", null)
        .where(
          tenantGrantExists(
            ctx,
            "tenant:spaces_read"
          ) as unknown as ExpressionFactory<"executionStats", Schema>
        )
        .orderBy("bucketAt", "asc")
  ),

  monitoringAiUsageDaily: defineTenantQuery(
    z.object({ since: z.number() }),
    ({ args, ctx }) =>
      zql.aiUsageDaily
        .where("day", ">=", args.since)
        .where(({ or, cmp, exists }) =>
          or(
            cmp("workflowId", "IS", null),
            exists("workflow", (w) =>
              w.where(canReadWorkflow(ctx)).where("deletedAt", "IS", null)
            )
          )
        )
        .related("workflow", (w) => w.related("space", withPersonalSpaceOwner))
  ),

  // Superseded by connectorUsageDailyLite (+ workflowSpaces); kept for cached
  // clients until the contract phase.
  connectorUsageDaily: defineTenantQuery(
    z.object({
      since: z.number(),
      measures: z.array(z.string()).optional(),
      connectorIds: z.array(z.string()).optional(),
    }),
    ({ args, ctx }) =>
      zql.connectorUsageDaily
        .where("day", ">=", args.since)
        .orderBy("day", "desc")
        .where((eb) =>
          eb.and(
            args.measures ? eb.cmp("measure", "IN", args.measures) : undefined,
            args.connectorIds
              ? eb.cmp("connectorId", "IN", args.connectorIds)
              : undefined
          )
        )
        .where(({ or, cmp, exists }) =>
          or(
            cmp("workflowId", "IS", null),
            exists("workflow", (w) =>
              w.where(canReadWorkflow(ctx)).where("deletedAt", "IS", null)
            )
          )
        )
        .related("workflow", (w) => w.related("space", withPersonalSpaceOwner))
  ),

  // No workflow relation — syncing workflow/space rows per usage row is
  // prohibitively expensive, so consumers join attribution client-side from
  // workflowSpaces.
  connectorUsageDailyLite: defineTenantQuery(
    z.object({
      since: z.number(),
      measures: z.array(z.string()).optional(),
      connectorIds: z.array(z.string()).optional(),
    }),
    ({ args, ctx }) =>
      zql.connectorUsageDaily
        .where("day", ">=", args.since)
        .orderBy("day", "desc")
        .where((eb) =>
          eb.and(
            args.measures ? eb.cmp("measure", "IN", args.measures) : undefined,
            args.connectorIds
              ? eb.cmp("connectorId", "IN", args.connectorIds)
              : undefined
          )
        )
        .where(({ or, cmp, exists }) =>
          or(
            cmp("workflowId", "IS", null),
            exists("workflow", (w) =>
              w.where(canReadWorkflow(ctx)).where("deletedAt", "IS", null)
            )
          )
        )
  ),

  workflowSpaces: defineTenantQuery(({ ctx }) =>
    zql.workflows.where(canReadWorkflow(ctx)).related("space")
  ),

  connectedAppForWorkflow: defineTenantQuery(
    z.object({ workflowId: z.string() }),
    ({ args, ctx }) =>
      zql.connectedApps
        .where("backingWorkflowId", args.workflowId)
        .where("fresh", false)
        .where(canReadConnector(ctx))
        .orderBy("createdAt", "desc")
        .one()
  ),

  loadConnectedApp: defineTenantQuery(
    z.object({ connectedAppId: z.string() }),
    ({ args, ctx }) =>
      zql.connectedApps
        .where("id", args.connectedAppId)
        .where(canReadConnector(ctx))
        .related("oauthConnection")
        .related("roleGrantsAsScope", roleGrantsWithMembers)
        // commit_step usages exist per historical commit and can be huge, so
        // sync only those at a live branch head — the usage tab diffs them
        // against the RBAC-scoped live usage to count hidden steps.
        .related("usages", (u) =>
          u.where(({ cmp, exists, or }) =>
            or(
              cmp("type", "!=", "commit_step"),
              exists("branchesAtHead", (b) =>
                b.whereExists("workflow", (w) =>
                  w.where("deletedAt", "IS", null)
                )
              )
            )
          )
        )
        .one()
  ),

  loadConnectorDetails: defineTenantQuery(
    z.object({ connectedAppId: z.string() }),
    ({ args, ctx }) =>
      zql.connectedApps
        .where("id", args.connectedAppId)
        .where(canReadConnector(ctx))
        .related("oauthConnection")
        .related("roleGrantsAsScope", roleGrantsWithMembers)
        .one()
  ),

  loadConnectorNonCommitUsageTotals: defineTenantQuery(
    z.object({ connectedAppId: z.string() }),
    ({ args, ctx }) =>
      zql.connectedAppUsages
        .where("connectedAppId", args.connectedAppId)
        .where("type", "!=", "commit_step")
        .whereExists("connectedApp", (a) => a.where(canReadConnector(ctx)))
  ),

  loadConnectorLiveUsageTotals: defineTenantQuery(
    z.object({ connectedAppId: z.string() }),
    ({ args, ctx }) =>
      zql.connectedAppUsages
        .where("connectedAppId", args.connectedAppId)
        .where("type", "commit_step")
        .whereExists("connectedApp", (a) => a.where(canReadConnector(ctx)))
        .whereExists("branchesAtHead", (b) =>
          b.whereExists("workflow", (w) => w.where("deletedAt", "IS", null))
        )
  ),

  loadConnectionClaim: defineTenantQuery(
    z.object({ claimId: z.string() }),
    ({ args, ctx }) =>
      zql.connectionClaims
        .where("id", args.claimId)
        .whereExists("workflow", (w) => w.where(canReadWorkflow(ctx)))
        .one()
  ),

  // Live workflow usage, one row per branch whose head commit uses the connector.
  loadConnectedAppLiveUsage: defineTenantQuery(
    z.object({ connectedAppId: z.string() }),
    ({ args, ctx }) =>
      zql.workflowBranches
        .whereExists("headCommit", (c) =>
          c.whereExists("appUsages", (u) =>
            u
              .where("type", "commit_step")
              .where("connectedAppId", args.connectedAppId)
          )
        )
        .whereExists("workflow", (w) =>
          w
            .where("deletedAt", "IS", null)
            .whereExists("space", (s) => s.where(canReadSpace(ctx)))
        )
        .related("headCommit", (c) =>
          c
            .related("appUsages", (u) =>
              u
                .where("type", "commit_step")
                .where("connectedAppId", args.connectedAppId)
            )
            .related("files", (f) =>
              f.where("path", "LIKE", "%/config.toml").related("blob")
            )
            .one()
        )
        .related("workflow", (w) =>
          w
            .related("space", withPersonalSpaceOwner)
            .related("branches")
            .related("stepActivity")
            .one()
        )
  ),

  // Personal-chat usages, filtered to chats the caller can read.
  loadConnectedAppPersonalChatUsage: defineTenantQuery(
    z.object({ connectedAppId: z.string() }),
    ({ args, ctx }) =>
      zql.connectedAppUsages
        .where("type", "personal_chat")
        .where("connectedAppId", args.connectedAppId)
        .where(canReadPersonalChat(ctx))
        .related("personalChat", (pc) => pc.related("user"))
  ),

  // Count-only source for personal chats a change would disconnect: bare usage
  // rows (no owner or title) for open chats owned by the given users, so the
  // caller can size chat impact it isn't allowed to see individually.
  connectorPersonalChatUsageForUsers: defineTenantQuery(
    z.object({ connectedAppId: z.string(), userIds: z.array(z.string()) }),
    ({ args, ctx }) =>
      zql.connectedAppUsages
        .where("type", "personal_chat")
        .where("connectedAppId", args.connectedAppId)
        .whereExists("connectedApp", (a) => a.where(canReadConnector(ctx)))
        .whereExists("personalChat", (pc) =>
          pc.where("userId", "IN", args.userIds).where("closedAt", "IS", null)
        )
  ),

  // Git-sync usages, filtered to spaces the caller can read.
  loadConnectedAppGitRemoteUsage: defineTenantQuery(
    z.object({ connectedAppId: z.string() }),
    ({ args, ctx }) =>
      zql.connectedAppUsages
        .where("type", "space_git_remote")
        .where("connectedAppId", args.connectedAppId)
        .whereExists("space", (s) => s.where(canReadSpace(ctx)))
        .related("space", withPersonalSpaceOwner)
        .related("spaceGitRemote")
  ),

  // Audit-log-destination usages, filtered to destinations the caller can read.
  loadConnectedAppAuditUsage: defineTenantQuery(
    z.object({ connectedAppId: z.string() }),
    ({ args, ctx }) =>
      zql.connectedAppUsages
        .where("type", "audit_log_destination")
        .where("connectedAppId", args.connectedAppId)
        .whereExists("auditLogDestination", (d) =>
          d.where(canReadAuditLogDestination(ctx))
        )
        .related("auditLogDestination")
  ),

  loadConnectedAppAiProviderUsage: defineTenantQuery(
    z.object({ connectedAppId: z.string() }),
    ({ args }) =>
      zql.connectedAppUsages
        .where("type", "ai_provider")
        .where("connectedAppId", args.connectedAppId)
        .related("aiProvider")
  ),

  // Commit-step usages of every connector granted to one of these groups,
  // restricted to readable spaces. Feeds the group-removal disconnect warning;
  // liveness (branch head) is decided client-side.
  groupConnectorLiveUsage: defineTenantQuery(
    z.object({ userGroupIds: z.array(z.string()) }),
    ({ args, ctx }) =>
      zql.connectedAppUsages
        .where("type", "commit_step")
        .whereExists("connectedApp", (a) =>
          a
            .where(canReadConnector(ctx))
            .whereExists("roleGrantsAsScope", (g) =>
              g.where("userGroupId", "IN", args.userGroupIds)
            )
        )
        .whereExists("commit", (c) =>
          c.whereExists("branch", (b) =>
            b.whereExists("workflow", (w) =>
              w
                .where("deletedAt", "IS", null)
                .whereExists("space", (s) => s.where(canReadSpace(ctx)))
            )
          )
        )
        .related("connectedApp", (a) =>
          a.related("roleGrantsAsScope", leanScopeGrants).one()
        )
        .related("commit", (c) =>
          c.related("branch", (b) => b.related("workflow").one()).one()
        )
  ),

  // Git-sync usages of every connector granted to one of these groups,
  // restricted to readable spaces.
  groupConnectorGitRemoteUsage: defineTenantQuery(
    z.object({ userGroupIds: z.array(z.string()) }),
    ({ args, ctx }) =>
      zql.connectedAppUsages
        .where("type", "space_git_remote")
        .whereExists("connectedApp", (a) =>
          a
            .where(canReadConnector(ctx))
            .whereExists("roleGrantsAsScope", (g) =>
              g.where("userGroupId", "IN", args.userGroupIds)
            )
        )
        .whereExists("space", (s) => s.where(canReadSpace(ctx)))
        .related("space", withPersonalSpaceOwner)
        .related("connectedApp", (a) =>
          a.related("roleGrantsAsScope", leanScopeGrants).one()
        )
  ),

  connectedAppGroupAccess: defineTenantQuery(
    z.object({ connectedAppId: z.string() }),
    ({ args, ctx }) =>
      zql.connectedApps
        .where("id", args.connectedAppId)
        .where(canReadConnector(ctx))
        .related("roleGrantsAsScope", (g) =>
          g
            .related("userGroup", (ug) => ug.related("roleGrantsAsPrincipal"))
            .related("user", (u) => u.related("roleGrantsAsPrincipal"))
        )
        .one()
  ),

  loadSpace: defineTenantQuery(
    z.object({ spaceId: z.string(), activitySince: z.number().optional() }),
    ({ args, ctx }) => {
      const activitySince = args.activitySince ?? Number.MAX_SAFE_INTEGER;
      return (
        zql.spaces
          .where("id", args.spaceId)
          .where(canReadSpace(ctx))
          .related("workflows", (w) =>
            w
              .where("deletedAt", "IS", null)
              .whereExists("branches")
              .orderBy("createdAt", "desc")
              .limit(100)
              .related("createdBy")
              .related("contributors", recentContributors(activitySince))
              .related("branches", workflowBranches(ctx.userId))
          )
          // TODO(expand/contract): remove this relation once no cached client
          // reads `space.roleGrantsAsScope`. Current readers (the space page and
          // SpaceSettingsForm) now source space grants from `roleGrantsByScope`,
          // so this is kept only to avoid changing the query shape for stale
          // clients mid-deploy.
          .related("roleGrantsAsScope", roleGrantsForAccessCheck(ctx.userId))
          .one()
      );
    }
  ),

  spaceUserStats: defineTenantQuery(
    z.object({ spaceId: z.string() }),
    ({ args, ctx }) =>
      zql.spaceUserStats
        .where("spaceId", args.spaceId)
        .whereExists("space", (s) => s.where(canReadSpace(ctx)))
        .related("user")
  ),

  spaceVolumes: defineTenantQuery(
    z.object({ spaceId: z.string(), branchId: z.string().optional() }),
    ({ args, ctx }) => {
      const base = zql.spaceVolumes
        .where("spaceId", args.spaceId)
        .whereExists("space", (s) => s.where(canReadSpace(ctx)))
        .related("branch", (b) => b.related("workflow").one())
        .orderBy("name", "asc");
      return args.branchId ? base.where("branchId", args.branchId) : base;
    }
  ),

  workflowBranchPagePreview: defineTenantQuery(
    z.object({ workflowId: z.string(), branchId: z.string() }),
    ({ args, ctx }) =>
      zql.workflows
        .where("id", args.workflowId)
        .where(canReadWorkflow(ctx))
        .related("space")
        .related("branches", (b) =>
          b
            .where("id", args.branchId)
            .related("headCommit", (c) =>
              c.related("routes").related("links").one()
            )
            .limit(1)
        )
        .one()
  ),

  workflowBranchRoutes: defineTenantQuery(
    z.object({
      workflowId: z.string(),
      branchId: z.string().optional(),
    }),
    ({ args, ctx }) =>
      zql.workflows
        .where("id", args.workflowId)
        .where(canReadWorkflow(ctx))
        .related("space")
        .related("branches", (b) =>
          (args.branchId
            ? b.where("id", args.branchId)
            : b.where("name", "main")
          )
            .related(
              "headCommit",
              (
                c: Query<"workflowCommits", Schema, WorkflowCommit | undefined>
              ) =>
                c
                  .related("routes")
                  .related("files", (f) =>
                    f.where("path", "LIKE", "%/config.toml").related("blob")
                  )
                  .one()
            )
            .one()
        )
        .one()
  ),

  workflowMainRoutes: defineTenantQuery(
    z.object({ workflowId: z.string() }),
    ({ args, ctx }) =>
      zql.workflows
        .where("id", args.workflowId)
        .where(canReadWorkflow(ctx))
        .related("space")
        .related("branches", (b) =>
          b
            .where("name", "main")
            .related(
              "headCommit",
              (
                c: Query<"workflowCommits", Schema, WorkflowCommit | undefined>
              ) => c.related("routes").one()
            )
            .one()
        )
        .one()
  ),

  spaceMainRoutes: defineTenantQuery(
    z.object({ spaceId: z.string() }),
    ({ args, ctx }) =>
      zql.spaces
        .where("id", args.spaceId)
        .where(canReadSpace(ctx))
        .related("workflows", (w) =>
          w.where("deletedAt", "IS", null).related("branches", (b) =>
            b
              .where("name", "main")
              .related(
                "headCommit",
                (
                  c: Query<
                    "workflowCommits",
                    Schema,
                    WorkflowCommit | undefined
                  >
                ) => c.related("routes").one()
              )
              .one()
          )
        )
        .one()
  ),

  loadWorkflow: defineTenantQuery(
    z.object({ workflowId: z.string() }),
    ({ args, ctx }) =>
      zql.workflows
        .where("id", args.workflowId)
        .where(canReadWorkflow(ctx))
        .related("space", withPersonalSpaceOwner)
        .related("branches", workflowBranchSummaries)
        .one()
  ),

  workflowPages: defineTenantQuery(
    z.object({ workflowId: z.string(), branchId: z.string().optional() }),
    ({ args, ctx }) =>
      zql.workflows
        .where("id", args.workflowId)
        .where(canReadWorkflow(ctx))
        .related("space", withPersonalSpaceOwner)
        .related("createdBy")
        .related("contributors", recentContributors(Number.MAX_SAFE_INTEGER))
        .related(
          "branches",
          workflowBranchesForLinks(ctx.userId, args.branchId)
        )
        .one()
  ),

  loadBranch: defineTenantQuery(
    z.object({
      workflowId: z.string(),
      branchId: z.string().optional(),
    }),
    ({ args, ctx }) => {
      const base = zql.workflowBranches
        .where("workflowId", args.workflowId)
        .where(canReadBranch(ctx))
        .related("user")
        .related("design")
        .related("contributors", (c) => c.related("user"))
        .related("commits", (c) => c.orderBy("createdAt", "asc"))
        .related("headCommit", (c) =>
          c
            .related("files", (f) => f.related("blob"))
            .related("routes")
            .one()
        );
      return (args.branchId ? base.where("id", args.branchId) : base)
        .orderBy("createdAt", "asc")
        .limit(1)
        .one();
    }
  ),

  loadMainBranch: defineTenantQuery(
    z.object({ workflowId: z.string() }),
    ({ args, ctx }) =>
      zql.workflowBranches
        .where("workflowId", args.workflowId)
        .where("name", "main")
        .where(canReadBranch(ctx))
        .one()
  ),

  loadBranchSummary: defineTenantQuery(
    z.object({ branchId: z.string() }),
    ({ args, ctx }) =>
      zql.workflowBranches
        .where("id", args.branchId)
        .where(canReadBranch(ctx))
        .one()
  ),

  personalChats: defineTenantQuery(
    z.object({ userId: z.string() }).optional(),
    ({ args, ctx }) =>
      zql.personalChats
        .where("userId", args?.userId ?? ctx.userId)
        .where("closedAt", "IS", null)
        .where(canReadPersonalChats(ctx))
        .orderBy("createdAt", "asc")
        .related("chat", (c) => c.related("entries", fullChatEntries).one())
  ),

  // Every readable open personal chat with no relations — for per-member
  // chat counts, not for rendering chats.
  allPersonalChats: defineTenantQuery(({ ctx }) =>
    zql.personalChats
      .where("closedAt", "IS", null)
      .where(canReadPersonalChats(ctx))
  ),

  personalVolumes: defineTenantQuery(({ ctx }) =>
    zql.personalVolumes.where("userId", ctx.userId).orderBy("name", "asc")
  ),

  personalChatTabs: defineTenantQuery(({ ctx }) =>
    zql.personalChats
      .where("userId", ctx.userId)
      .where("closedAt", "IS", null)
      .related("chat")
  ),

  closedPersonalChats: defineTenantQuery(
    z.object({ userId: z.string() }).optional(),
    ({ args, ctx }) =>
      zql.personalChats
        .where("userId", args?.userId ?? ctx.userId)
        .where("closedAt", "IS NOT", null)
        .where(canReadPersonalChats(ctx))
        .related("chat", (c) => c.related("entries"))
        .orderBy("closedAt", "desc")
  ),

  loadPersonalChat: defineTenantQuery(
    z.object({ personalChatId: z.string() }),
    ({ args, ctx }) =>
      zql.personalChats
        .where("id", args.personalChatId)
        .where(canReadPersonalChats(ctx))
        .related("chat", (c) => c.related("entries", fullChatEntries).one())
        .related("appUsages", (u) =>
          u
            .where("type", "personal_chat")
            .related("connectedApp", (ca) => ca.related("oauthConnection"))
        )
        .one()
  ),

  loadCommitChat: defineTenantQuery(
    z.object({ commitId: z.string() }),
    ({ args, ctx }) =>
      args.commitId
        ? zql.chats
            .where("commitId", args.commitId)
            .whereExists(
              "commit",
              (c) =>
                c.whereExists(
                  "branch",
                  (b) =>
                    b.whereExists(
                      "workflow",
                      (w) =>
                        w.whereExists(
                          "space",
                          (s) => s.where(canReadSpace(ctx)),
                          { scalar: true }
                        ),
                      { scalar: true }
                    ),
                  { scalar: true }
                ),
              { scalar: true }
            )
            .orderBy("createdAt", "desc")
            .related("entries", fullChatEntries)
        : zql.chats.where("commitId", "").related("entries", fullChatEntries)
  ),

  loadBranchChats: defineTenantQuery(
    z.object({ branchId: z.string() }),
    ({ args, ctx }) =>
      args.branchId
        ? zql.workflowCommits
            .where("branchId", args.branchId)
            .whereExists(
              "branch",
              (b) =>
                b.whereExists(
                  "workflow",
                  (w) =>
                    w.whereExists("space", (s) => s.where(canReadSpace(ctx)), {
                      scalar: true,
                    }),
                  { scalar: true }
                ),
              { scalar: true }
            )
            .related("chats", (c) =>
              c.related("entries", fullChatEntries).one()
            )
            .orderBy("createdAt", "asc")
        : zql.workflowCommits
            .where("id", "")
            .related("chats", (c) =>
              c.related("entries", fullChatEntries).one()
            )
  ),

  loadWorkflowRun: defineTenantQuery(
    z.object({ runId: z.string() }),
    ({ args, ctx }) =>
      zql.workflowRuns.where("id", args.runId).where(canReadRun(ctx)).one()
  ),

  loadBranchWorkflowRuns: defineTenantQuery(
    z.object({ branchId: z.string() }),
    ({ args, ctx }) =>
      args.branchId
        ? zql.workflowRuns
            .where("branchId", args.branchId)
            .where("ephemeral", false)
            .where(canReadRun(ctx))
            .orderBy("createdAt", "desc")
            .limit(WORKFLOW_RUNS_LIMIT)
        : zql.workflowRuns.where("id", "")
  ),

  loadBranchRunsSummary: defineTenantQuery(
    z.object({ branchId: z.string() }),
    ({ args, ctx }) =>
      args.branchId
        ? zql.workflowRuns
            .where("branchId", args.branchId)
            .where(canReadRun(ctx))
            .orderBy("createdAt", "desc")
            .limit(WORKFLOW_RUNS_LIMIT)
        : zql.workflowRuns.where("id", "")
  ),

  loadRunSummary: defineTenantQuery(
    z.object({ runId: z.string() }),
    ({ args, ctx }) =>
      zql.workflowRuns.where("id", args.runId).where(canReadRun(ctx)).one()
  ),

  loadAllCommitFiles: defineTenantQuery(
    z.object({ commitId: z.string() }),
    ({ args, ctx }) =>
      args.commitId
        ? zql.commitFiles
            .where("commitId", args.commitId)
            .whereExists(
              "commit",
              (c) =>
                c.whereExists(
                  "branch",
                  (b) =>
                    b.whereExists(
                      "workflow",
                      (w) =>
                        w.whereExists(
                          "space",
                          (s) => s.where(canReadSpace(ctx)),
                          { scalar: true }
                        ),
                      { scalar: true }
                    ),
                  { scalar: true }
                ),
              { scalar: true }
            )
            .related("blob")
        : zql.commitFiles.where("commitId", "").related("blob")
  ),

  loadStepActivity: defineTenantQuery(
    z.object({ workflowId: z.string(), branchId: z.string() }),
    ({ args, ctx }) =>
      zql.stepActivity
        .where("workflowId", args.workflowId)
        .where("branchId", args.branchId)
        .whereExists("workflow", (w) => w.where(canReadWorkflow(ctx)), {
          scalar: true,
        })
  ),

  loadStepActivityForStep: defineTenantQuery(
    z.object({
      workflowId: z.string(),
      branchId: z.string(),
      stepDir: z.string(),
    }),
    ({ args, ctx }) =>
      zql.stepActivity
        .where("workflowId", args.workflowId)
        .where("branchId", args.branchId)
        .where("stepDir", args.stepDir)
        .whereExists("workflow", (w) => w.where(canReadWorkflow(ctx)), {
          scalar: true,
        })
        .one()
  ),

  loadBranchStepActivity: defineTenantQuery(
    z.object({ branchId: z.string() }),
    ({ args, ctx }) =>
      zql.stepActivity
        .where("branchId", args.branchId)
        .whereExists("workflow", (w) => w.where(canReadWorkflow(ctx)), {
          scalar: true,
        })
  ),

  userRecentWorkflows: defineTenantQuery(
    z.object({ activitySince: z.number().optional() }),
    ({ args, ctx }) => {
      const activitySince = args.activitySince ?? Number.MAX_SAFE_INTEGER;
      return zql.workflowViews
        .where("userId", ctx.userId)
        .where(canReadWorkflowView(ctx))
        .whereExists("workflow", (w) =>
          w.where("deletedAt", "IS", null).whereExists("branches")
        )
        .related("workflow", (w) =>
          w
            .related("space", withPersonalSpaceOwner)
            .related("createdBy")
            .related("favorites", (f) => f.where("userId", ctx.userId).limit(1))
            .related("contributors", recentContributors(activitySince))
            .related("branches", workflowBranches(ctx.userId))
            // TODO(expand/contract): remove once no cached client reads the
            // workflow-level `connectedApps`; chips now read branch head usages.
            .related("connectedApps")
        )
        .orderBy("lastSeenAt", "desc")
        .limit(50);
    }
  ),

  userBranches: defineTenantQuery(
    z.object({ activitySince: z.number() }),
    ({ args, ctx }) =>
      listRowBranch(args.activitySince)(zql.workflowBranches)
        .where("userId", ctx.userId)
        .where(canReadBranch(ctx))
        .related("workflow", (w) =>
          w
            .related("space", withPersonalSpaceOwner)
            .related("createdBy")
            .related("branches")
        )
        .orderBy("lastChatEntryAt", "desc")
        .limit(50)
  ),

  userWorkflowViews: defineTenantQuery(({ ctx }) =>
    zql.workflowViews.where("userId", ctx.userId)
  ),

  navBranches: defineTenantQuery(({ ctx }) =>
    zql.workflowBranches
      .where("userId", ctx.userId)
      .where(canReadBranch(ctx))
      .related("workflow")
      .orderBy("lastChatEntryAt", "desc")
      .limit(50)
  ),

  navSpace: defineTenantQuery(
    z.object({ spaceId: z.string() }),
    ({ args, ctx }) =>
      zql.spaces
        .where("id", args.spaceId)
        .where(canReadSpace(ctx))
        .related("roleGrantsAsScope", (rg) => rg.related("user").limit(1))
        .one()
  ),

  userFavoriteWorkflowIds: defineTenantQuery(({ ctx }) =>
    zql.workflowFavorites
      .where("userId", ctx.userId)
      .where(canReadWorkflowFavorite(ctx))
  ),

  userFavoriteWorkflows: defineTenantQuery(
    z.object({ activitySince: z.number().optional() }),
    ({ args, ctx }) => {
      const activitySince = args.activitySince ?? Number.MAX_SAFE_INTEGER;
      return zql.workflowFavorites
        .where("userId", ctx.userId)
        .where(canReadWorkflowFavorite(ctx))
        .whereExists("workflow", (w) => w.whereExists("branches"))
        .related("workflow", (w) =>
          w
            .related("space", withPersonalSpaceOwner)
            .related("createdBy")
            .related("contributors", recentContributors(activitySince))
            .related("branches", workflowBranches(ctx.userId))
        )
        .orderBy("createdAt", "desc")
        .limit(50);
    }
  ),

  // All users can read AI providers (api_key is a non-replicated secret column);
  // managing them requires tenant:ai_providers_manage, enforced in the mutations.
  aiConfigs: defineTenantQuery(() =>
    zql.aiProviders
      .orderBy("createdAt", "asc")
      .related("appUsage", (u) => u.related("connectedApp"))
  ),

  activeAIProvider: defineTenantQuery(() =>
    zql.aiProviders.where("active", true).one()
  ),

  aiModelPricingRows: defineTenantQuery(() =>
    zql.aiModelPricing.orderBy("modelId", "asc")
  ),

  monitoringAiBudgets: defineTenantQuery(() => zql.aiBudgets),

  monitoringUsageBudgets: defineTenantQuery(() => zql.connectorUsageBudgets),

  monitoringUsagePrices: defineTenantQuery(() => zql.connectorUsagePrices),

  monitorAiProviders: defineTenantQuery(() =>
    zql.aiProviders
      .orderBy("createdAt", "asc")
      .related("modelPricing")
      .related("budgets")
      .related("appUsage")
  ),

  loadAiProvider: defineTenantQuery(
    z.object({ aiProviderId: z.string() }),
    ({ args }) =>
      zql.aiProviders
        .where("id", args.aiProviderId)
        .related("appUsage", (u) => u.related("connectedApp"))
        .one()
  ),

  monitoringWorkflowValue: defineTenantQuery(({ ctx }) =>
    zql.workflows
      .where(canReadWorkflow(ctx))
      .where("deletedAt", "IS", null)
      .whereExists("branches", (b) =>
        b.where("name", "main").where("headCommitId", "IS NOT", null)
      )
      .related("space", withPersonalSpaceOwner)
      .related("branches", (b) => b.where("name", "main"))
  ),

  branchModelPreference: defineTenantQuery(
    z.object({ branchId: z.string() }),
    ({ args, ctx }) =>
      zql.branchModelPreferences
        .where("branchId", args.branchId)
        .where("userId", ctx.userId)
        .one()
  ),

  ssoProviders: defineTenantQuery(({ ctx }) =>
    zql.ssoProvider.where(canReadSsoProvider(ctx))
  ),

  oidcProviders: defineTenantQuery(({ ctx }) =>
    zql.oidcProviders.where(canReadOidcProvider(ctx)).orderBy("name", "asc")
  ),

  // Tenant-admin list of registered OAuth apps (tier 1).
  oauthApps: defineTenantQuery(({ ctx }) =>
    zql.oauthClient.where(canReadOAuthApp(ctx)).orderBy("name", "asc")
  ),

  // A single registered OAuth app with the service accounts it's authorized
  // for, for the app detail/connection view. Same read gate as the list.
  oauthApp: defineTenantQuery(z.object({ id: z.string() }), ({ args, ctx }) =>
    zql.oauthClient
      .where("id", args.id)
      .where(canReadOAuthApp(ctx))
      .related("serviceAccountAuthorizations", (a) =>
        a.related("serviceAccount")
      )
      .one()
  ),

  serviceAccounts: defineTenantQuery(({ ctx }) =>
    zql.serviceAccounts
      .related("federations", (f) => f.related("provider"))
      .related("apiKeys", (k) => k.where(isHumanManagedServiceAccountKey()))
      .related("roleGrantsAsPrincipal", (g) => g.related("scopeAsSpace"))
      .where(canReadServiceAccount(ctx))
      .orderBy("name", "asc")
  ),

  auditLogs: defineTenantQuery(
    z.object({ limit: z.number() }),
    ({ args, ctx }) =>
      zql.auditLogs
        .orderBy("timestamp", "desc")
        .limit(args.limit)
        .where(canReadAuditLog(ctx))
  ),

  auditLogDestinations: defineTenantQuery(({ ctx }) =>
    zql.auditLogDestinations
      .orderBy("name", "asc")
      .related("appUsage", (u) => u.related("connectedApp"))
      .where(canReadAuditLogDestination(ctx))
  ),

  loadSpaceGitRemote: defineTenantQuery(
    z.object({ spaceId: z.string() }),
    ({ args, ctx }) =>
      zql.spaceGitRemotes
        .where("spaceId", args.spaceId)
        .whereExists("space", (s) => s.where(canReadSpace(ctx)), {
          scalar: true,
        })
        .related("appUsage", (u) => u.related("connectedApp"))
        .one()
  ),

  // Open GitHub PRs on the git branch that this workflow branch maps to,
  // reached through its space branch (space_branch_members.workflowBranchId).
  openPullRequestsForBranch: defineTenantQuery(
    z.object({ workflowBranchId: z.string() }),
    ({ args, ctx }) =>
      zql.spaceGitPullRequests
        .whereExists("space", (s) => s.where(canReadSpace(ctx)))
        .whereExists("spaceBranch", (b) =>
          b.whereExists("members", (m) =>
            m.where("workflowBranchId", args.workflowBranchId)
          )
        )
        .orderBy("updatedAt", "desc")
  ),

  // The git branch this workflow branch maps to; absent until a sync has
  // reconciled the branch onto the remote.
  gitBranchForWorkflowBranch: defineTenantQuery(
    z.object({ workflowBranchId: z.string() }),
    ({ args, ctx }) =>
      zql.spaceBranches
        .whereExists("space", (s) => s.where(canReadSpace(ctx)))
        .whereExists("members", (m) =>
          m.where("workflowBranchId", args.workflowBranchId)
        )
        .one()
  ),

  featureFlags: defineTenantQuery(() =>
    zql.featureFlags
      .where("enabledAt", "IS NOT", null)
      .orderBy("createdAt", "desc")
      .related("createdBy")
  ),

  allFeatureFlags: defineTenantQuery(() =>
    zql.featureFlags.orderBy("createdAt", "desc").related("createdBy")
  ),

  networks: defineTenantQuery(({ ctx }) =>
    zql.networks
      .orderBy("createdAt", "desc")
      .related("createdBy")
      .related("egressRoutes")
      .where(canReadNetwork(ctx))
  ),

  egressRules: defineTenantQuery(({ ctx }) =>
    zql.egressRules
      .where("disabledAt", "IS", null)
      .orderBy("createdAt", "desc")
      .where(canReadEgressRule(ctx))
  ),

  networkAccess: defineTenantQuery(
    z.object({ networkId: z.string() }),
    ({ args, ctx }) =>
      zql.networks
        .where("id", args.networkId)
        .where(canReadNetwork(ctx))
        .related("roleGrantsAsScope", (g) =>
          g.related("customRole", (cr) => cr.related("rolePermissions"))
        )
        .one()
  ),

  loadNetwork: defineTenantQuery(
    z.object({ networkId: z.string() }),
    ({ args, ctx }) =>
      zql.networks
        .where("id", args.networkId)
        .where(canReadNetwork(ctx))
        .related("createdBy")
        .related("roleGrantsAsScope", roleGrantsWithMembers)
        .one()
  ),

  networkGroupAccess: defineTenantQuery(
    z.object({ networkId: z.string() }),
    ({ args, ctx }) =>
      zql.networks
        .where("id", args.networkId)
        .where(canReadNetwork(ctx))
        .related("roleGrantsAsScope", (g) =>
          g
            .related("userGroup", (ug) => ug.related("roleGrantsAsPrincipal"))
            .related("user", (u) => u.related("roleGrantsAsPrincipal"))
        )
        .one()
  ),

  apiKeys: defineTenantQuery(({ ctx }) =>
    zql.apikey
      .orderBy("createdAt", "desc")
      .related("space", withPersonalSpaceOwner)
      .related("user")
      .where(canReadApiKey(ctx))
      // Service account credentials (service-account keys and UI-issued
      // sessions) live on the Service accounts page; excluding them keeps this
      // list — and the settings page's visibility — scoped to the legacy
      // user/space keys being deprecated, so minting a service account
      // credential can't resurrect the page.
      .where("serviceAccountId", "IS", null)
      // Hide everything that isn't a legacy user/space key this page is for: the
      // short-lived creds auth flows mint (`device:` / `federated:` /
      // `usersession:`) and the member-modal user keys (`userkey:`,
      // createUserApiKey), so creating one of those can't resurrect the page.
      // Keep in sync if those names change.
      .where(({ and, or, cmp }) =>
        and(
          or(cmp("name", "IS", null), cmp("name", "NOT LIKE", "device:%")),
          or(
            cmp("name", "IS", null),
            cmp("name", "NOT LIKE", `${FEDERATED_API_KEY_NAME_PREFIX}%`)
          ),
          or(cmp("name", "IS", null), cmp("name", "NOT LIKE", "usersession:%")),
          or(
            cmp("name", "IS", null),
            cmp("name", "NOT LIKE", `${USER_API_KEY_NAME_PREFIX}%`)
          )
        )
      )
  ),

  // A member's member-modal API keys (name-prefixed `userkey:` by
  // createUserApiKey), for the member modal's key list. `canReadApiKey` gates it
  // to the member themselves or a tenant user-manager, so an admin can review
  // (and revoke) another member's keys.
  userApiKeys: defineTenantQuery(
    z.object({ userId: z.string() }),
    ({ ctx, args: { userId } }) =>
      zql.apikey
        .where("referenceId", userId)
        .where(canReadApiKey(ctx))
        .where("serviceAccountId", "IS", null)
        .where("name", "LIKE", `${USER_API_KEY_NAME_PREFIX}%`)
        .orderBy("createdAt", "desc")
  ),

  deletedWorkflows: defineTenantQuery(
    z.object({ spaceId: z.string() }),
    ({ ctx, args: { spaceId } }) =>
      zql.workflows
        .where("spaceId", spaceId)
        .where(canReadDeletedWorkflow(ctx))
        .related("branches")
        .orderBy("deletedAt", "desc")
        .limit(200)
  ),

  allUserGroups: defineTenantQuery(() =>
    zql.userGroups
      .related("roleGrantsAsScope", (mg) => mg.related("user"))
      .related("roleGrantsAsPrincipal")
      .orderBy("name", "asc")
  ),

  allUserGroupsLite: defineTenantQuery(() =>
    zql.userGroups.orderBy("name", "asc")
  ),

  currentUserGroups: defineTenantQuery(({ ctx }) =>
    zql.userGroups
      .whereExists("roleGrantsAsScope", (mg) => mg.where("userId", ctx.userId))
      .related("roleGrantsAsScope", (mg) => mg.related("user"))
      .related("roleGrantsAsPrincipal")
      .orderBy("name", "asc")
  ),

  userGroupManagerGrants: defineTenantQuery(() =>
    zql.roleGrants
      .where(({ or, cmp, exists }) =>
        or(
          cmp("builtinRoleId", "user_group_manager"),
          exists("customRole", (cr) =>
            cr.whereExists("rolePermissions", (rp) =>
              rp.where("permission", "user_group:write")
            )
          )
        )
      )
      .related("userGroup")
      .related("user")
      .related("customRole", (cr) => cr.related("rolePermissions"))
  ),

  allRoles: defineTenantQuery(() =>
    zql.roles
      .related("rolePermissions")
      .related("roleGrants")
      .orderBy("name", "asc")
  ),

  allRolesLite: defineTenantQuery(() =>
    zql.roles.related("rolePermissions").orderBy("name", "asc")
  ),

  selectableUserGroups: defineTenantQuery(() =>
    zql.userGroups
      .related("roleGrantsAsScope", (mg) => mg.related("user"))
      .orderBy("name", "asc")
  ),

  tenantAdminGrants: defineTenantQuery(({ ctx }) =>
    zql.roleGrants
      .where("builtinRoleId", "IN", [...ALLOW_ALL_ROLE_IDS])
      .where("scopeId", ctx.tenantId)
      .where("userId", "IS NOT", null)
      .related("user")
  ),

  roleGrantsByScope: defineTenantQuery(
    z.object({ scopeId: z.string() }),
    ({ args, ctx }) =>
      roleGrantsForAccessCheck(ctx.userId)(
        zql.roleGrants.where("scopeId", args.scopeId)
      )
  ),

  currentUserGrants: defineTenantQuery(({ ctx }) =>
    zql.roleGrants
      .where(({ or, cmp, exists }) =>
        or(
          cmp("userId", ctx.userId),
          exists("userGroup", (ug) =>
            ug.whereExists("roleGrantsAsScope", (mg) =>
              mg.where("userId", ctx.userId)
            )
          )
        )
      )
      .related("customRole", (cr) => cr.related("rolePermissions"))
  ),

  roleGrantsByUser: defineTenantQuery(
    z.object({ userId: z.string() }),
    ({ args }) =>
      zql.roleGrants.where("userId", args.userId).related("customRole")
  ),

  roleGrantsByPrincipalGroup: defineTenantQuery(
    z.object({ userGroupId: z.string() }),
    ({ args }) => zql.roleGrants.where("userGroupId", args.userGroupId)
  ),

  roleGrantsByPrincipalServiceAccount: defineTenantQuery(
    z.object({ serviceAccountId: z.string() }),
    ({ args, ctx }) =>
      zql.roleGrants
        .where("serviceAccountId", args.serviceAccountId)
        .whereExists("serviceAccount", (sa) =>
          sa.where(canReadServiceAccount(ctx))
        )
        .related("customRole", (cr) => cr.related("rolePermissions"))
  ),

  serviceAccountKeys: defineTenantQuery(
    z.object({ serviceAccountId: z.string() }),
    ({ args, ctx }) =>
      zql.apikey
        .where("serviceAccountId", args.serviceAccountId)
        .whereExists("serviceAccount", (sa) =>
          sa.where(canReadServiceAccount(ctx))
        )
        .where(isHumanManagedServiceAccountKey())
        .orderBy("createdAt", "desc")
  ),

  // OAuth apps a service-account manager has authorized to act as this account
  // (tier 2). Joined to the oauthApps list by clientId in the UI.
  oauthAppAuthorizationsForServiceAccount: defineTenantQuery(
    z.object({ serviceAccountId: z.string() }),
    ({ args, ctx }) =>
      zql.oauthClientServiceAccount
        .where("serviceAccountId", args.serviceAccountId)
        .whereExists("serviceAccount", (sa) =>
          sa.where(canReadServiceAccount(ctx))
        )
        .orderBy("createdAt", "desc")
  ),

  // Active (non-revoked) OAuth grants acting as this service account: the live
  // credentials an SA manager can see and revoke.
  oauthGrantsForServiceAccount: defineTenantQuery(
    z.object({ serviceAccountId: z.string() }),
    ({ args, ctx }) =>
      zql.oauthGrant
        .where("serviceAccountId", args.serviceAccountId)
        .where("revokedAt", "IS", null)
        .whereExists("serviceAccount", (sa) =>
          sa.where(canReadServiceAccount(ctx))
        )
        .orderBy("createdAt", "desc")
  ),

  // The caller's own active user-acting OAuth grants (null service account):
  // third parties they connected that act as them, shown in profile so the user
  // can revoke access.
  myOAuthGrants: defineTenantQuery(({ ctx }) =>
    zql.oauthGrant
      .where("userId", ctx.userId)
      .where("serviceAccountId", "IS", null)
      .where("revokedAt", "IS", null)
      .related("client")
      .orderBy("createdAt", "desc")
  ),

  allDirectUserGrants: defineTenantQuery(() =>
    zql.roleGrants.where("userId", "IS NOT", null)
  ),

  allDirectServiceAccountGrants: defineTenantQuery(() =>
    zql.roleGrants
      .where("serviceAccountId", "IS NOT", null)
      .related("serviceAccount")
  ),
});
